import React, { createContext, useContext, useEffect, useState } from "react";
import {
  fetchAuthSession,
  getCurrentUser,
  signOut,
  resetPassword,
  confirmResetPassword,
} from "aws-amplify/auth";

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within an AdminAuthProvider");
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const REMEMBER_ME_KEY = "trivistaRememberMe";

  const clearRememberMeIfExpired = async () => {
    if (typeof window === "undefined") return;

    try {
      const raw = window.localStorage.getItem(REMEMBER_ME_KEY);
      if (!raw) return;

      const data = JSON.parse(raw);
      if (!data?.enabled || !data?.expiresAt) return;

      const now = Date.now();
      if (now > data.expiresAt) {
        await signOut();
        window.localStorage.removeItem(REMEMBER_ME_KEY);
        setAdminUser(null);
      }
    } catch (error) {
      console.error("Error handling admin remember me expiry:", error);
    }
  };

  useEffect(() => {
    checkAdminAuthStatus();
  }, []);

  const checkAdminAuthStatus = async () => {
    try {
      await clearRememberMeIfExpired();

      const session = await fetchAuthSession();
      
      if (!session.tokens?.accessToken) {
        setAdminUser(null);
        return false;
      }

      // Check if user is in admin group
      const groups = session.tokens.accessToken.payload["cognito:groups"] || [];
      const isAdmin = groups.includes("Admins");

      if (isAdmin) {
        // Only get current user if we have a valid session
        const currentUser = await getCurrentUser();
        setAdminUser(currentUser);
        return true;
      } else {
        console.log("User is not an admin");
        setAdminUser(null);
        return false;
      }
    } catch (error) {
      console.log("Admin auth check error:", error);
      setAdminUser(null);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const adminLogout = async () => {
    try {
      await signOut();
      setAdminUser(null);
    } catch (error) {
      console.error("Error signing out admin:", error);
    }
  };

  // Request a password reset code
  const passwordRecovery = async (email) => {
    try {
      await resetPassword(email);
    } catch (err) {
      console.error("Error initiating password recovery:", err);
      throw err;
    }
  };

  // Confirm code + set new password
  const passwordReset = async (email, code, newPassword) => {
    try {
      await confirmResetPassword(email, code, newPassword);
    } catch (err) {
      console.error("Error resetting password:", err);
      throw err;
    }
  };

  const value = {
    adminUser,
    loading,
    adminLogout,
    checkAdminAuthStatus,
    passwordRecovery,
    passwordReset,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
